export function normalizeAttendanceSettings(course = {}) {
  const graceAbsencesRaw = Number(course.attendance_grace_absences);
  const minContributionRaw = Number(course.attendance_min_contribution);
  const penaltyIncreaseRaw = Number(course.attendance_penalty_increase_percent);

  return {
    graceAbsences:
      Number.isFinite(graceAbsencesRaw) && graceAbsencesRaw >= 0
        ? Math.floor(graceAbsencesRaw)
        : 0,
    minContribution:
      Number.isFinite(minContributionRaw)
        ? minContributionRaw
        : 0,
    penaltyPattern:
      course.attendance_penalty_pattern === 'increasing'
        ? 'increasing'
        : 'linear',
    penaltyIncreasePercent:
      Number.isFinite(penaltyIncreaseRaw) && penaltyIncreaseRaw >= 0
        ? penaltyIncreaseRaw
        : 25
  };
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function getPenaltyFraction({
  penalizedAbsences,
  penalizableAbsences,
  penaltyPattern,
  penaltyIncreasePercent
}) {
  if (penalizableAbsences <= 0 || penalizedAbsences <= 0) {
    return 0;
  }

  if (penalizedAbsences >= penalizableAbsences) {
    return 1;
  }

  if (
    penaltyPattern !== 'increasing' ||
    !Number.isFinite(Number(penaltyIncreasePercent)) ||
    Number(penaltyIncreasePercent) <= 0
  ) {
    return penalizedAbsences / penalizableAbsences;
  }

  const ratio = 1 + Number(penaltyIncreasePercent) / 100;
  const numerator = Math.pow(ratio, penalizedAbsences) - 1;
  const denominator = Math.pow(ratio, penalizableAbsences) - 1;

  if (!Number.isFinite(numerator) || !Number.isFinite(denominator) || denominator <= 0) {
    return penalizedAbsences / penalizableAbsences;
  }

  return clamp(numerator / denominator, 0, 1);
}

export function calculateAttendanceContribution({
  absences,
  totalRequired,
  weight,
  graceAbsences = 0,
  minContribution = 0,
  penaltyPattern = 'linear',
  penaltyIncreasePercent = 25
}) {
  const safeWeight = Number.isFinite(Number(weight)) ? Number(weight) : 0;
  const safeMinimum = Number.isFinite(Number(minContribution))
    ? Number(minContribution)
    : 0;
  const safeTotal = Math.max(0, Math.floor(Number(totalRequired) || 0));
  const safeAbsences = clamp(
    Math.max(0, Math.floor(Number(absences) || 0)),
    0,
    safeTotal
  );
  const safeGrace = Math.max(0, Math.floor(Number(graceAbsences) || 0));

  if (safeTotal === 0) {
    return safeWeight;
  }

  const effectiveGrace = Math.min(safeGrace, safeTotal);
  const penalizableAbsences = safeTotal - effectiveGrace;

  if (penalizableAbsences <= 0 || safeAbsences <= effectiveGrace) {
    return safeWeight;
  }

  const penalizedAbsences = Math.min(
    safeAbsences - effectiveGrace,
    penalizableAbsences
  );

  const penaltyFraction = getPenaltyFraction({
    penalizedAbsences,
    penalizableAbsences,
    penaltyPattern,
    penaltyIncreasePercent
  });

  return safeWeight -
    (safeWeight - safeMinimum) * penaltyFraction;
}

export function buildAttendanceContributionRows({
  totalRequired,
  weight,
  graceAbsences = 0,
  minContribution = 0,
  penaltyPattern = 'linear',
  penaltyIncreasePercent = 25
}) {
  const safeTotal = Math.max(0, Math.floor(Number(totalRequired) || 0));
  const rows = [];

  for (let absences = 0; absences <= safeTotal; absences += 1) {
    rows.push({
      absences,
      contribution: calculateAttendanceContribution({
        absences,
        totalRequired: safeTotal,
        weight,
        graceAbsences,
        minContribution,
        penaltyPattern,
        penaltyIncreasePercent
      })
    });
  }

  return rows;
}

export function formatAttendanceContribution(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '0%';

  const rounded = Math.round((Math.abs(numeric) < 0.0000001 ? 0 : numeric) * 10) / 10;
  const text = Number.isInteger(rounded)
    ? rounded.toFixed(0)
    : rounded.toFixed(1);

  return `${text}%`;
}

export function buildAttendanceContributionTableHtml({
  totalRequired,
  weight,
  graceAbsences = 0,
  minContribution = 0,
  penaltyPattern = 'linear',
  penaltyIncreasePercent = 25
}) {
  const safeTotal = Math.max(0, Math.floor(Number(totalRequired) || 0));

  if (safeTotal === 0) {
    return '<div style="margin:10px 0;color:#627d98;">No attendance requirements are currently assigned.</div>';
  }

  const rows = buildAttendanceContributionRows({
    totalRequired: safeTotal,
    weight,
    graceAbsences,
    minContribution,
    penaltyPattern,
    penaltyIncreasePercent
  });

  const body = rows
    .map(row => `
      <tr>
        <td style="border:1px solid #cbd5df;padding:6px 10px;text-align:center;">${row.absences}</td>
        <td style="border:1px solid #cbd5df;padding:6px 10px;text-align:center;">${formatAttendanceContribution(row.contribution)}</td>
      </tr>
    `)
    .join('');

  return `
    <div style="margin:12px 0;">
      <table style="border-collapse:collapse;width:100%;max-width:520px;font-size:0.95em;">
        <thead>
          <tr>
            <th style="border:1px solid #cbd5df;padding:7px 10px;background:#f3f6f8;text-align:center;">Absences</th>
            <th style="border:1px solid #cbd5df;padding:7px 10px;background:#f3f6f8;text-align:center;">Contribution to final grade</th>
          </tr>
        </thead>
        <tbody>${body}</tbody>
      </table>
      <div style="margin-top:6px;font-size:0.85em;color:#627d98;">Based on ${safeTotal} required class${safeTotal === 1 ? '' : 'es'}.</div>
    </div>
  `;
}